import React from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { ArrowRight, FileStack, Edit3, MessageSquare } from 'lucide-react';
import { Loading } from '@/components/common/F1States';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/components/portal/Notifications';
import ProjectHero from '@/components/portal/premium/ProjectHero';
import DecisionsRail, { useDecisions } from '@/components/portal/premium/DecisionsRail';
import * as P from '@/lib/portal/service';

const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

function Stat({ icon: Icon, value, label, testId }) {
  return <div className="py-4 px-2"><div className="font-display font-semibold text-xl text-[#3A3936]" data-testid={testId}>{value}</div><div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1"><Icon size={11} /> {label}</div></div>;
}

function ProjectCard({ p, unread, index }) {
  const reg = useSWR(['register', p.id], () => P.register(p.id));
  const d = useDecisions(p.id, p.my_role);
  const iss = useSWR(['cv-issues', p.id], () => P.issues(p.id), { shouldRetryOnError: false });
  const rf = useSWR(['rfis', p.id], () => P.rfis(p.id));
  const current = (reg.data || []).filter((r) => r.is_current).length;
  const attention = d.total + (unread || 0);
  return (
    <Link to={`/portal/projects/${p.id}`} data-testid={`portal-project-${p.project_code}`} style={{ animationDelay: `${index * 90}ms` }} className="group block rounded-2xl bg-white border border-[#D8D0C4] hover:border-[#957C5D] hover:shadow-[0_18px_40px_-24px_rgba(58,57,54,0.35)] transition-[border-color,box-shadow] duration-300 overflow-hidden animate-in fade-in slide-in-from-bottom-2 fill-mode-both">
      <ProjectHero p={p} />
      {attention > 0 && <div data-testid="project-actions" className="px-6 py-3 bg-[#957C5D]/10 border-b border-[#957C5D]/20 text-sm text-[#6B4F3A] flex items-center justify-between"><span>{attention} item{attention > 1 ? 's' : ''} need your attention</span><ArrowRight size={14} /></div>}
      <div className="grid grid-cols-3 divide-x divide-[#D8D0C4] text-center">
        <Stat icon={FileStack} value={current} label="Current drawings" />
        <Stat icon={Edit3} value={P.currentlyApproved(iss.data).length} label="Approved variations" testId="approved-variations-count" />
        <Stat icon={MessageSquare} value={(rf.data || []).filter((r) => r.status !== 'closed').length} label="Open queries" />
      </div>
      <div className="px-6 py-4 border-t border-[#D8D0C4] flex items-center justify-between text-sm"><span className="text-muted-foreground">{P.roleLabel(p.my_role)}</span><span className="text-[#957C5D] inline-flex items-center gap-1 group-hover:gap-2 transition-all duration-200">Open project <ArrowRight size={14} /></span></div>
    </Link>
  );
}

export default function PortalHome() {
  const { user } = useAuth();
  const projects = useSWR('portal-projects', P.myProjects);
  const first = projects.data?.[0];
  const ppl = useSWR(first ? ['people', first.id] : null, () => P.people(first.id));
  const n = useNotifications();
  const unreadBy = (n.data || []).filter((x) => !x.read_at).reduce((m, x) => ({ ...m, [x.project_id]: (m[x.project_id] || 0) + 1 }), {});
  const me = (ppl.data || []).find((x) => x.user_id === user?.id);
  const name = (me?.display_name || user?.user_metadata?.full_name || '').split(' ')[0];
  const list = projects.data || [];
  return (
    <div data-testid="portal-home" className="space-y-10">
      <div className="pt-2">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#957C5D] font-semibold">Alvora Construction · Project portal</div>
        <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl text-[#3A3936] mt-3 leading-[1.02]" data-testid="portal-greeting">{greeting()}{name ? `, ${name}` : ''}.</h1>
        <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-xl">{list.length === 1 ? 'Your project at a glance — site photography, decisions awaiting you, drawings and progress.' : 'Your projects at a glance — site photography, decisions awaiting you, drawings and progress.'}</p>
      </div>
      {list.map((p) => <DecisionsRail key={p.id} projectId={p.id} role={p.my_role} compact />)}
      {projects.isLoading ? <Loading /> : !list.length
        ? <div className="rounded-2xl border border-dashed border-[#D8D0C4] bg-white p-16 text-center" data-testid="portal-projects-empty"><div className="font-display font-semibold text-lg text-[#3A3936]">No projects shared with you yet</div><p className="text-sm text-muted-foreground mt-1">When Alvora shares a project, it will appear here.</p></div>
        : <div className={`grid gap-8 ${list.length > 1 ? 'md:grid-cols-2' : 'max-w-3xl'}`}>{list.map((p, i) => <ProjectCard key={p.id} p={p} unread={unreadBy[p.id]} index={i} />)}</div>}
    </div>
  );
}
